import type Alpine from 'alpinejs';

import {
    decodeProps,
    dataAttributeForSchemaProp,
    type CodecFn,
    type PropChange,
    type SchemaToProps,
} from './codecs';
import {
    createTransitionCoordinator,
    type AnyTransitionHook,
    type TransitionCallback,
    type TransitionCoordinator,
    type TransitionForHook,
} from './transition-coordinator';
import type {Machine, MachineEvent, MachineSnapshot} from './state-machine';
import {isSerializableEqual} from '../utils';

type MachineElement = HTMLElement & Alpine.ElementWithXAttributes;
type MachineSchema = Record<string, CodecFn<unknown>>;
type MachineProps<TSchema extends MachineSchema> = SchemaToProps<TSchema>;
type TransitionHookLike = {
    name: string;
    state: string;
    project?: (...args: never[]) => unknown;
    projectEach?: (...args: never[]) => readonly unknown[];
    onDone: string;
    onError: string;
};
type HookName<THooks extends readonly {name: string}[]> = THooks[number]['name'];
type HookForName<THooks, TName> = Extract<THooks extends readonly unknown[] ? THooks[number] : never, {name: TName}>;

type AlpinePropsChangedEvent<TSchema extends MachineSchema> = {
    type: 'PROPS_CHANGED';
    props: MachineProps<TSchema>;
    changes: Array<PropChange<TSchema>>;
};

type MachineFactoryScope<TSchema extends MachineSchema> = {
    el: MachineElement;
    props: MachineProps<TSchema>;
};

type CreateAlpineMachineOptions<
    TContext,
    TSchema extends MachineSchema,
    THooks extends readonly TransitionHookLike[],
> = {
    schema: TSchema;
    hooks: THooks;
    createMachine(scope: MachineFactoryScope<TSchema>): Machine<TContext, MachineProps<TSchema>>;
    resolveTransitionElement?(scope: Readonly<{
        name: string;
        data: unknown;
        root: MachineElement;
        registrationEl: MachineElement;
    }>): MachineElement | null | undefined;
};

type AlpineMachineOwner<TProps = unknown> = {
    invalidateDom(): void;
    onTransition<TData>(
        name: string,
        callback: TransitionCallback<TProps, TData, MachineElement>,
        el?: MachineElement,
    ): () => void;
};

type AlpineMachine<
    TContext,
    TProps,
    THooks extends readonly TransitionHookLike[],
> = Omit<AlpineMachineOwner<TProps>, 'onTransition'> & {
    snapshot: {current: MachineSnapshot<TContext, TProps> | null};
    connect(
        el: MachineElement,
        snapshot?: {current: MachineSnapshot<TContext, TProps> | null},
    ): void;
    disconnect(): void;
    getSnapshot(): MachineSnapshot<TContext, TProps>;
    getCommittedSnapshot(): MachineSnapshot<TContext, TProps>;
    send(event: MachineEvent): void;
    matches(state: string): boolean;
    hasTag(tag: string): boolean;
    onTransition<TName extends HookName<THooks>>(
        name: TName,
        callback: (transition: TransitionForHook<HookForName<THooks, TName>, MachineElement>) => unknown | Promise<unknown>,
        el?: MachineElement,
    ): () => void;
};

type PendingTransition<TProps> = {
    name: string;
    callback: TransitionCallback<TProps, unknown, MachineElement>;
    el?: MachineElement;
    unregister?: () => void;
    active: boolean;
};

const alpineMachineOwner = Symbol.for('trellis-ui.alpine-machine-owner');
const owners = new WeakMap<Element, AlpineMachineOwner>();

const findAlpineMachineOwner = (
    AlpineInstance: Alpine.Alpine,
    el: Element,
): AlpineMachineOwner | undefined => {
    const dataStack = typeof AlpineInstance.closestDataStack === 'function'
        ? AlpineInstance.closestDataStack(el as Alpine.ElementWithXAttributes)
        : [];
    const scopedOwner = dataStack.find((scope) => Reflect.get(scope, alpineMachineOwner) === true);
    if (scopedOwner) return scopedOwner as AlpineMachineOwner;

    const owner = AlpineInstance.findClosest(el, (candidate) => owners.has(candidate));
    return owner ? owners.get(owner) : undefined;
};

const createAlpineMachine = <
    TContext,
    TSchema extends MachineSchema,
    const THooks extends readonly TransitionHookLike[],
>({
    schema,
    hooks,
    createMachine: machineFactory,
    resolveTransitionElement,
}: CreateAlpineMachineOptions<TContext, TSchema, THooks>): AlpineMachine<TContext, MachineProps<TSchema>, THooks> => {
    type TProps = MachineProps<TSchema>;

    const snapshot: {current: MachineSnapshot<TContext, TProps> | null} = {current: null};
    let snapshotMirror = snapshot;
    const pending: PendingTransition<TProps>[] = [];
    let root: MachineElement | null = null;
    let machine: Machine<TContext, TProps> | null = null;
    let coordinator: TransitionCoordinator<TProps, MachineElement> | null = null;
    let observer: MutationObserver | null = null;
    let unsubscribeSnapshot: (() => void) | null = null;
    let destroyed = false;

    const requireMachine = (): Machine<TContext, TProps> => {
        if (!machine) throw new Error('Alpine machine has not been initialized.');
        return machine;
    };

    const registerPending = (registration: PendingTransition<TProps>): void => {
        if (!coordinator || !root || !registration.active) return;
        registration.unregister = coordinator.register({
            name: registration.name,
            el: registration.el ?? root,
            callback: registration.callback,
        });
    };

    const component: AlpineMachine<TContext, TProps, THooks> = {
        snapshot,
        invalidateDom(): void {
            if (destroyed || !machine) return;
            machine.send('DOM_INVALIDATED');
        },
        connect(el, reactiveSnapshot = snapshot): void {
            if (destroyed) throw new Error('Alpine machine has been destroyed.');
            if (machine) return;

            root = el;
            snapshotMirror = reactiveSnapshot;
            let props = decodeProps({el: root, schema});
            const createdMachine = machineFactory({el: root, props});
            let createdCoordinator: TransitionCoordinator<TProps, MachineElement>;
            try {
                createdCoordinator = createTransitionCoordinator<TContext, TProps, MachineElement>({
                    machine: createdMachine,
                    hooks: hooks as unknown as readonly AnyTransitionHook<TContext, TProps>[],
                    root,
                    ...(resolveTransitionElement ? {resolveElement: resolveTransitionElement} : {}),
                });
            } catch (error) {
                createdMachine.stop();
                root = null;
                throw error;
            }
            machine = createdMachine;
            coordinator = createdCoordinator;
            snapshotMirror.current = machine.getSnapshot();
            unsubscribeSnapshot = machine.subscribe((nextSnapshot) => {
                snapshotMirror.current = nextSnapshot;
            });
            owners.set(root, component as unknown as AlpineMachineOwner);
            pending.forEach(registerPending);

            const authoritativePropByAttribute = new Map(Object.entries(schema).flatMap(([key, codec]) => codec.meta.attribute ? [[
                dataAttributeForSchemaProp(key, codec),
                key as keyof TProps & string,
            ] as const] : []));
            observer = new MutationObserver((mutations) => {
                if (!root || !machine) return;
                const touched = new Set(mutations.flatMap(({attributeName}) => {
                    const key = attributeName ? authoritativePropByAttribute.get(attributeName) : undefined;
                    return key ? [key] : [];
                }));
                const nextProps = decodeProps({el: root, schema});
                const changes = Object.keys(nextProps).flatMap((key) => {
                    const typedKey = key as keyof TProps & string;
                    if (!touched.has(typedKey) && isSerializableEqual(props[typedKey], nextProps[typedKey])) return [];
                    return [{
                        key: typedKey,
                        previous: props[typedKey],
                        value: nextProps[typedKey],
                        source: 'attribute' as const,
                    } as PropChange<TSchema>];
                });
                if (changes.length === 0) return;
                props = nextProps;
                machine.send({type: 'PROPS_CHANGED', props: nextProps, changes});
            });
            observer.observe(root, {
                attributes: true,
                attributeFilter: Object.entries(schema).map(([key, codec]) => dataAttributeForSchemaProp(key, codec)),
            });
        },
        disconnect(): void {
            if (destroyed) return;
            destroyed = true;
            observer?.disconnect();
            observer = null;
            unsubscribeSnapshot?.();
            unsubscribeSnapshot = null;
            coordinator?.destroy();
            coordinator = null;
            machine?.stop();
            machine = null;
            if (root) owners.delete(root);
            Reflect.deleteProperty(component, alpineMachineOwner);
            root = null;
            pending.splice(0).forEach((registration) => registration.unregister?.());
            snapshotMirror.current = null;
            snapshotMirror = snapshot;
        },
        getSnapshot(): MachineSnapshot<TContext, TProps> {
            const current = snapshotMirror.current;
            if (!current) throw new Error('Alpine machine has not been initialized.');
            return current;
        },
        getCommittedSnapshot(): MachineSnapshot<TContext, TProps> {
            return requireMachine().getSnapshot();
        },
        send(event): void {
            requireMachine().send(event);
        },
        matches(state): boolean {
            return requireMachine().matches(state);
        },
        hasTag(tag): boolean {
            return requireMachine().hasTag(tag);
        },
        onTransition<TName extends HookName<THooks>>(
            name: TName,
            callback: (transition: TransitionForHook<HookForName<THooks, TName>, MachineElement>) => unknown | Promise<unknown>,
            el?: MachineElement,
        ): () => void {
            if (destroyed) throw new Error('Alpine machine has been destroyed.');
            const registration: PendingTransition<TProps> = {
                name,
                callback: callback as TransitionCallback<TProps, unknown, MachineElement>,
                active: true,
            };
            if (el) registration.el = el;
            pending.push(registration);
            registerPending(registration);

            return () => {
                if (!registration.active) return;
                registration.active = false;
                registration.unregister?.();
                delete registration.unregister;
                const index = pending.indexOf(registration);
                if (index >= 0) pending.splice(index, 1);
            };
        },
    };

    Reflect.defineProperty(component, alpineMachineOwner, {
        value: true,
        configurable: true,
    });
    return component;
};

export {createAlpineMachine, findAlpineMachineOwner};
export type {
    AlpineMachine,
    AlpineMachineOwner,
    AlpinePropsChangedEvent,
    CreateAlpineMachineOptions,
    MachineFactoryScope,
};
